import React from 'react';
import {
  Header,
  SpaceBetween,
  Box,
  TextContent,
  ColumnLayout,
  AnchorNavigation,
  AppLayout,
  ContentLayout,
  Grid,
  ExpandableSection,
  Container,
} from '@cloudscape-design/components';
import { useTheme } from '../../context/ThemeContext';
import { useModuleVisit } from '../../hooks/useAnalytics';

const DEFAULT_BANNER = "linear-gradient(135deg, #1a1a2e 0%, #16213e 40%, #0f3460 100%)";
const SUMMIT_BANNER_LIGHT = "center/cover url('/summit/2025_Summits_Kaltura_SessionPage_SessionThumbnail_1@2x.png')";
const SUMMIT_BANNER_DARK = "center/cover url('/summit/2025_Summits_Kaltura_SessionPage_SessionThumbnail_2.png')";

function BuildVsBuyPageContent() {
  const { isSummit, isDark } = useTheme();
  useModuleVisit('build_vs_buy');
  const banner = isSummit ? (isDark ? SUMMIT_BANNER_DARK : SUMMIT_BANNER_LIGHT) : DEFAULT_BANNER;

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.9rem',
  };

  const thStyle = {
    textAlign: 'left',
    padding: '10px 14px',
    borderBottom: `2px solid ${isDark ? '#475569' : '#d1d5db'}`,
    backgroundColor: isDark ? '#1e293b' : '#f2f3f3',
    fontWeight: 600,
  };

  const tdStyle = {
    padding: '10px 14px',
    borderBottom: `1px solid ${isDark ? '#334155' : '#e9ebed'}`,
    verticalAlign: 'top',
  };

  return (
    <ContentLayout
      defaultPadding
      disableOverlap
      headerBackgroundStyle={banner}
      headerVariant="high-contrast"
      header={
        <Box padding={{ vertical: "xxxl" }}>
          <Grid gridDefinition={[{ colspan: { default: 12, s: 8 } }]}>
            <Box padding="s">
              <Box
                fontSize="display-l"
                fontWeight="bold"
                variant="h1"
                padding="n"
              >
                Build vs Buy:
                Custom AI Agent Systems
              </Box>
              <Box
                variant="p"
                color="text-body-secondary"
                margin={{ top: "xs", bottom: "l" }}
              >
                Understanding the strategic value of building custom AI agents on your platform
                versus purchasing generic SaaS offerings — and when each approach makes sense.
              </Box>
            </Box>
          </Grid>
        </Box>
      }
    >
      <SpaceBetween size="xxl">

        {/* Section 1: Why Build? */}
        <div id="why-build">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <hr style={{ border: 'none', borderTop: '1px solid #e9ebed', margin: '3.5rem 0 3.5rem 0' }} />
            <Header variant="h1" margin={{ bottom: "l" }}>
              Why Build Custom AI Agents?
            </Header>
          </Box>

          <Container 
            header={<Header variant="h2">🔒 Your Data. Your Models. Your Boundary.</Header>}
          >
            <div style={{ borderLeft: `4px solid ${isDark ? '#60a5fa' : '#0073bb'}`, paddingLeft: '1.25rem' }}>
              <TextContent>
                <p style={{ fontSize: '1.15rem', lineHeight: '1.7' }}>
                  When you build custom AI agents on your own infrastructure, your sensitive data — financial reports, 
                  proprietary research, internal IP — <strong>never leaves your AWS account</strong>. No third-party 
                  processors. No data shipped to external APIs. No trusting another organisation's security posture 
                  with your most valuable information.
                </p>
                <p style={{ marginTop: '1rem' }}>
                  For organisations handling regulated data — financial services, healthcare, government — this isn't 
                  a nice-to-have. It's a requirement. Building gives you full control over encryption, access policies, 
                  data retention, audit trails, and compliance posture. You decide where your data lives, who can 
                  access it, and how long it's retained.
                </p>
                <p style={{ marginTop: '1rem' }}>
                  With a SaaS AI tool, you're sending your documents to a third party for processing. Even with 
                  contractual guarantees, you've expanded your data boundary and introduced a dependency on someone 
                  else's security practices. For IP-sensitive workloads, that's a risk many organisations aren't 
                  willing to take.
                </p>
              </TextContent>
            </div>
          </Container>

          <Box margin={{ top: "xl", bottom: "m" }}>
            <Header variant="h2">Supporting Advantages</Header>
            <TextContent>
              <p>Beyond data sovereignty, building custom agents unlocks additional strategic benefits:</p>
            </TextContent>
          </Box>

          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <TextContent>
                <h3>🎯 Full Workflow Customisation</h3>
                <p>
                  Design agent graphs, prompts, and tool integrations that match your exact business processes. 
                  No compromising on "close enough" — build precisely what your domain requires.
                </p>

                <h3>🔄 Model Choice & Flexibility</h3>
                <p>
                  Use any model from any provider — Amazon Nova, Anthropic Claude, Meta Llama, or your own 
                  fine-tuned models. Switch providers without rewriting your application.
                </p>

                <h3>🔗 Deep Integration</h3>
                <p>
                  Connect directly to your enterprise systems — databases, APIs, internal tools, knowledge bases. 
                  No waiting for a vendor to build a connector.
                </p>
              </TextContent>
            </div>
            <div>
              <TextContent>
                <h3>💰 Cost Optimisation at Scale</h3>
                <p>
                  Pay for compute, not per-seat licensing. As usage grows, costs scale with actual consumption 
                  rather than user count. Optimise spend by right-sizing models and implementing caching.
                </p>

                <h3>🚀 Iterate & Evolve</h3>
                <p>
                  Ship improvements on your timeline, not the vendor's roadmap. Add new agents, swap models, 
                  refine prompts, and extend capabilities whenever you need to.
                </p>

                <h3>🏁 Competitive Differentiation</h3>
                <p>
                  Custom-built AI creates unique capabilities that competitors can't simply purchase. 
                  If everyone uses the same SaaS tool, there's no differentiation.
                </p>
              </TextContent>
            </div>
          </ColumnLayout>

          <Box margin={{ top: "xl" }}>
            <Container header={<Header variant="h2">🔮 Art of the Possible: Agents That Talk to Each Other</Header>}>
              <TextContent>
                <p>
                  When you build your own agents, you're not limited to a single application. Custom agents can 
                  communicate across systems — a document analysis agent extracts key findings, passes them to a 
                  market data enrichment agent, which feeds a portfolio insights agent, which triggers an alert 
                  generation agent. Multi-system workflows emerge naturally when your agents share a common framework.
                </p>
                <p>
                  Standards like <strong>Agent-to-Agent (A2A)</strong> protocol and <strong>Model Context Protocol (MCP)</strong> make 
                  this interoperability practical today. Your research agents can collaborate with agents built by 
                  other teams or departments — each maintaining their own boundaries while working together on 
                  complex workflows that span multiple business functions.
                </p>
                <p>
                  This kind of cross-system orchestration is difficult to achieve with standalone SaaS products, 
                  which tend to operate in isolation. When you build, every agent you create becomes a building 
                  block for the next workflow — and the value compounds over time.
                </p>
              </TextContent>
            </Container>
          </Box>
        </div>

        {/* Section 2: Buy Limitations */}
        <div id="buy-limitations">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <hr style={{ border: 'none', borderTop: '1px solid #e9ebed', margin: '3.5rem 0 3.5rem 0' }} />
            <Header variant="h1" margin={{ bottom: "l" }}>
              Trade-Offs of SaaS AI Products
            </Header>
          </Box>

          <TextContent>
            <p>
              SaaS AI products offer real value — fast time-to-start, managed infrastructure, and proven workflows. 
              However, as AI becomes more central to business operations, it's worth understanding the trade-offs 
              that come with that convenience.
            </p>
          </TextContent>

          <Box margin={{ top: "l" }}>
            <ColumnLayout columns={2} variant="text-grid">
              <div>
                <TextContent>
                  <h3>📋 Predefined Workflows</h3>
                  <p>
                    SaaS products are designed for broad applicability. This means your specific business 
                    processes and terminology may need to adapt to the tool's model rather than the other way 
                    around. Customisation is typically limited to what the vendor exposes.
                  </p>

                  <h3>🌐 Data Processing Boundaries</h3>
                  <p>
                    Your data is processed on the vendor's infrastructure. For many use cases this is perfectly 
                    acceptable, but for sensitive IP or regulated data, it's an additional consideration in your 
                    risk assessment.
                  </p>

                  <h3>🔐 Platform Dependency</h3>
                  <p>
                    Over time, workflows and institutional knowledge become tied to the vendor's platform. 
                    This isn't inherently negative, but it's worth factoring into long-term planning — 
                    especially if the capability becomes business-critical.
                  </p>
                </TextContent>
              </div>
              <div>
                <TextContent>
                  <h3>🔌 Integration Scope</h3>
                  <p>
                    Integration options are typically limited to the vendor's connector ecosystem. Deep, 
                    bidirectional integration with proprietary enterprise systems may require workarounds 
                    or may not be feasible within the product's architecture.
                  </p>

                  <h3>📈 Pricing at Scale</h3>
                  <p>
                    Per-seat or per-query pricing models are straightforward to budget initially, but costs 
                    can grow significantly with adoption. There's typically less ability to optimise underlying 
                    compute costs as usage scales.
                  </p>

                  <h3>🏁 Shared Capabilities</h3>
                  <p>
                    When multiple organisations use the same product, the AI capabilities are similar across 
                    all of them. For commodity tasks this is fine — but for core differentiators, it's worth 
                    considering whether custom development creates more strategic value.
                  </p>
                </TextContent>
              </div>
            </ColumnLayout>
          </Box>
        </div>

        {/* Section 3: Decision Framework */}
        <div id="decision-framework">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <hr style={{ border: 'none', borderTop: '1px solid #e9ebed', margin: '3.5rem 0 3.5rem 0' }} />
            <Header variant="h1" margin={{ bottom: "l" }}>
              Decision Framework
            </Header>
          </Box>

          <TextContent>
            <p>
              The build vs buy decision isn't binary — it depends on how central the capability is to your 
              business differentiation, your engineering capacity, and your timeline. Here's a framework 
              for making the right call.
            </p>
          </TextContent>

          <Box margin={{ top: "l" }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Criteria</th>
                    <th style={thStyle}>Build (Custom)</th>
                    <th style={thStyle}>Buy (SaaS)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>Workflow customisation</td>
                    <td style={tdStyle}>Full control — design agent graphs, prompts, tools</td>
                    <td style={tdStyle}>Limited to vendor's predefined workflows</td>
                  </tr>
                  <tr>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>Data sovereignty</td>
                    <td style={tdStyle}>Data stays in your AWS account</td>
                    <td style={tdStyle}>Data processed by third-party</td>
                  </tr>
                  <tr>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>Model choice</td>
                    <td style={tdStyle}>Any model, any provider, swap freely</td>
                    <td style={tdStyle}>Locked to vendor's model selection</td>
                  </tr>
                  <tr>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>Integration depth</td>
                    <td style={tdStyle}>Direct API/SDK integration with enterprise systems</td>
                    <td style={tdStyle}>Limited to vendor's connector ecosystem</td>
                  </tr>
                  <tr>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>Cost model</td>
                    <td style={tdStyle}>Pay for compute — scales with usage</td>
                    <td style={tdStyle}>Per-seat/per-query — scales with users</td>
                  </tr>
                  <tr>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>Time to first value</td>
                    <td style={tdStyle}>Longer initial build, faster iteration after</td>
                    <td style={tdStyle}>Fast initial setup, slower customisation</td>
                  </tr>
                  <tr>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>Maintenance</td>
                    <td style={tdStyle}>Team owns updates and operations</td>
                    <td style={tdStyle}>Vendor handles maintenance</td>
                  </tr>
                  <tr>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>Differentiation</td>
                    <td style={tdStyle}>Unique capability — competitive advantage</td>
                    <td style={tdStyle}>Same tool available to competitors</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Box>

          <Box margin={{ top: "xl" }}>
            <ColumnLayout columns={2} variant="text-grid">
              <Container header={<Header variant="h3">✅ Build When...</Header>}>
                <TextContent>
                  <ul>
                    <li>The capability is a <strong>core business differentiator</strong></li>
                    <li>You operate in a <strong>regulated industry</strong> with strict data requirements</li>
                    <li>Your workflows are <strong>complex and domain-specific</strong></li>
                    <li>You need <strong>deep integration</strong> with proprietary systems</li>
                    <li>You want to <strong>optimise costs</strong> as usage scales</li>
                    <li>You have (or can hire) <strong>engineering capacity</strong></li>
                    <li>You need to <strong>iterate quickly</strong> on AI capabilities</li>
                  </ul>
                </TextContent>
              </Container>
              <Container header={<Header variant="h3">🛒 Buy When...</Header>}>
                <TextContent>
                  <ul>
                    <li>The capability is <strong>not a core differentiator</strong> for your business</li>
                    <li>You need something <strong>running immediately</strong> — days, not weeks</li>
                    <li>Your use case is <strong>well-served by existing products</strong> that meet your requirements</li>
                    <li>You have <strong>limited engineering capacity</strong> for AI development right now</li>
                    <li>You're <strong>prototyping or validating</strong> a concept before committing to build</li>
                    <li>The <strong>maintenance burden</strong> outweighs the customisation benefit for this use case</li>
                    <li>Data sensitivity requirements are <strong>manageable</strong> within the vendor's model</li>
                    <li>An existing tool <strong>already fits your requirements</strong> without significant compromise</li>
                  </ul>
                </TextContent>
              </Container>
            </ColumnLayout>
          </Box>
        </div>

        {/* Section 4: ResearchIQ Case Study */}
        <div id="researchiq-case-study">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <hr style={{ border: 'none', borderTop: '1px solid #e9ebed', margin: '3.5rem 0 3.5rem 0' }} />
            <Header variant="h1" margin={{ bottom: "l" }}>
              ResearchIQ: A Build Example
            </Header>
          </Box>

          <TextContent>
            <p>
              ResearchIQ demonstrates what becomes possible when you build custom AI agents tailored to a 
              specific domain. Every aspect of the system — from agent specialisation to citation tracking 
              to report generation — is designed for financial document research. This level of domain 
              specificity isn't achievable with generic SaaS tools.
            </p>
          </TextContent>

          <Box margin={{ top: "l" }}>
            <ColumnLayout columns={2} variant="text-grid">
              <div>
                <TextContent>
                  <h3>What Building Enabled</h3>
                  <ul>
                    <li><strong>Multi-agent graph workflow</strong> — deterministic DAG execution with specialised agents for research, evidence compilation, and citation tracking</li>
                    <li><strong>Custom citation system</strong> — tracks attribution type (verbatim, paraphrased, computed) with page-level source references</li>
                    <li><strong>Domain-specific prompts</strong> — agents tuned for financial terminology, report structures, and analytical frameworks</li>
                    <li><strong>Knowledge Base integration</strong> — direct connection to Amazon Bedrock Knowledge Bases with custom retrieval strategies</li>
                    <li><strong>Real-time progress tracking</strong> — WebSocket-based updates showing each agent's progress through the workflow</li>
                    <li><strong>Live market data integration</strong> — agents tap into real-time market data APIs to enrich research with current pricing, trends, and sentiment</li>
                  </ul>
                </TextContent>
              </div>
              <div>
                <TextContent>
                  <h3>Technology Stack</h3>
                  <ul>
                    <li><strong>Strands Agents SDK</strong> — open-source framework for multi-agent orchestration with Graph pattern</li>
                    <li><strong>Amazon Bedrock</strong> — managed foundation model access with no infrastructure to manage</li>
                    <li><strong>Amazon Nova Pro</strong> — high-performance model optimised for complex reasoning tasks</li>
                    <li><strong>Bedrock Knowledge Bases</strong> — managed RAG with automatic chunking, embedding, and retrieval</li>
                    <li><strong>CloudWatch & X-Ray</strong> — production monitoring with OpenTelemetry-based tracing</li>
                    <li><strong>AWS CDK</strong> — infrastructure as code for repeatable, auditable deployments</li>
                  </ul>
                </TextContent>
              </div>
            </ColumnLayout>
          </Box>

          <Box margin={{ top: "l" }}>
            <ExpandableSection
              headerText="What a SaaS Alternative Would Miss"
              variant="container"
              headerDescription="Capabilities that require custom development"
            >
              <TextContent>
                <p>A generic document AI SaaS product would typically provide:</p>
                <ul>
                  <li>Basic document Q&A with a single model</li>
                  <li>Generic summarisation without domain awareness</li>
                  <li>Simple source attribution (document name only)</li>
                  <li>Fixed output formats</li>
                </ul>
                <p style={{ marginTop: '1rem' }}>What it would <strong>not</strong> provide:</p>
                <ul>
                  <li>Multi-agent specialisation (different agents for different research tasks)</li>
                  <li>Deterministic execution graphs with guaranteed processing order</li>
                  <li>Citation attribution types (verbatim vs paraphrased vs computed)</li>
                  <li>Custom executive briefing formats with data visualisations</li>
                  <li>Integration with your specific compliance and governance requirements</li>
                  <li>Model flexibility — ability to swap between Nova, Claude, or custom fine-tuned models per agent</li>
                  <li>Cost control — optimise token usage per agent, implement caching strategies</li>
                </ul>
              </TextContent>
            </ExpandableSection>
          </Box>
        </div>

      </SpaceBetween>
    </ContentLayout>
  );
}

// Tools component for "On this page" navigation
function BuildVsBuyPageTools() {
  return (
    <div>
      <Header variant="h3">
        <span id="navigation-header">On this page</span>
      </Header>
      <AnchorNavigation
        anchors={[
          {
            text: "Why Build Custom AI Agents?",
            href: "#why-build",
            level: 1
          },
          {
            text: "Trade-Offs of SaaS AI",
            href: "#buy-limitations",
            level: 1
          },
          {
            text: "Decision Framework",
            href: "#decision-framework",
            level: 1
          },
          {
            text: "ResearchIQ: A Build Example",
            href: "#researchiq-case-study",
            level: 1
          }
        ]}
        ariaLabelledby="navigation-header"
      />
    </div>
  );
}

function BuildVsBuyPage() {
  return (
    <AppLayout
      content={<BuildVsBuyPageContent />}
      tools={<BuildVsBuyPageTools />}
      toolsHide={false}
      navigationHide={true}
    />
  );
}

export default BuildVsBuyPage;
